import { ChargerHistoryAttributes } from '../../../models/charger-history-model';
import { ChargerAttributes } from '../../../models/charger-model';
import { getDateFromUtcSeconds } from './date-time-conversions';

// Type for the bit field
type HexWeek = string;

// Type for day ranges
export interface DayRange {
  start: number;
  end: number;
}

// Type for the week's configuration
export interface WeekConfiguration {
  [day: string]: DayRange;
}

export const dayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const createEmptyWeek = (
  overrides?: Partial<WeekConfiguration>
): WeekConfiguration => ({
  Sunday: { start: 0, end: 0 },
  Monday: { start: 0, end: 0 },
  Tuesday: { start: 0, end: 0 },
  Wednesday: { start: 0, end: 0 },
  Thursday: { start: 0, end: 0 },
  Friday: { start: 0, end: 0 },
  Saturday: { start: 0, end: 0 },
  ...overrides,
});

// Function to set peak hours for multiple days
// The input to setPeak will be
// {
// monday: {startTime: 13, endTime: 19},
// tuesday: {startTime: 13, endTime: 19},
// ...
// saturday:  {startTime: 0, endTime: 0}
// sunday: {startTime: 0, endTime: 0}
// }
export const setPeak = (weekConfig: WeekConfiguration): HexWeek => {
  const numBlocks = 28; // 7 days * 4 blocks per day (25 bits per day)
  const view = new Uint8Array(numBlocks);

  dayNames.forEach((day, index) => {
    const { start, end } = weekConfig[day];
    if (start === end) return; // Skip days with no peak hours.

    const dayBaseIndex = index * 4; // Each day now has 4 blocks
    const startBlock = Math.floor(start / 8);
    const endBlock = Math.floor(end / 8);

    for (let block = startBlock; block <= endBlock; block++) {
      const blockStartHour = block * 8;
      let mask = 0xff; // Start with all bits set

      if (block == startBlock) {
        mask = 0xff >> start % 8; // Zero out bits before the start hour in the block
      }
      if (block == endBlock) {
        mask &= ~(0xff >> ((end % 8) + 1));
      }

      view[dayBaseIndex + block] |= mask;
    }
  });

  const mappedBinary = [...view]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('');
  return mappedBinary;
};

export const checkIsPeak = (date: Date, weeklyHours: HexWeek): boolean => {
  const day = date.getDay(); // Get the day index, where 0 is Sunday
  const hour = date.getHours(); // Get the hour
  let binaryString = '';
  if (!weeklyHours) return false;
  for (let i = 0; i < weeklyHours.length; i += 2) {
    const hexValue = weeklyHours.substring(i, i + 2);
    const binaryChunk = parseInt(hexValue, 16).toString(2).padStart(8, '0');
    binaryString += binaryChunk;
  }
  //console.log("Complete binary sequence:", binaryString);

  // Calculate the exact bit index for the current hour
  const bitIndex = day * 25 + hour; // 25 hours per day (0-24), 1 bit per hour
  //console.log("Bit index = ", bitIndex);

  // Determine if the specific hour is peak by checking the bit at the bitIndex
  return binaryString.charAt(bitIndex) === '1';
};

// Calculate peak and off-peak times between two dates
// calculatePeak Function: Calculates the total number of
// seconds spent in peak and off-peak times between two
// Date instances. It iterates over every hour (or part thereof)
// between the start and end times,
// using the checkIsPeak function to check each hour.
export const calculatePeak = (
  startDateISO: string,
  endDateISO: string,
  weeklyHours: HexWeek
): { peakTime: number; offPeakTime: number } => {
  let peakSeconds = 0;
  let offPeakSeconds = 0;
  const startDate = new Date(startDateISO);
  const endDate = new Date(endDateISO);
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const secondsToEndOfHour =
      3600 - currentDate.getMinutes() * 60 - currentDate.getSeconds();
    const durationThisHour = Math.min(
      secondsToEndOfHour,
      (endDate.getTime() - currentDate.getTime()) / 1000
    );

    if (checkIsPeak(currentDate, weeklyHours)) {
      peakSeconds += durationThisHour;
      //console.log(`Adding to peak: ${durationThisHour} seconds at ${currentDate}`);
    } else {
      offPeakSeconds += durationThisHour;
      //console.log(`Adding to off-peak: ${durationThisHour} seconds at ${currentDate}`);
    }

    // Move to the next hour
    currentDate = new Date(currentDate.getTime() + secondsToEndOfHour * 1000);
    //console.log("now using current date:", currentDate);
  }

  return {
    peakTime: peakSeconds, //in seconds
    offPeakTime: offPeakSeconds, //in seconds
  };
};

export const generatePeakObj = (mappedBinary: HexWeek) => {
  // Detect format
  const isNewFormat = mappedBinary.length === 56; // 28 bytes * 2 hex chars
  const blocksPerDay = isNewFormat ? 4 : 3;
  const hoursPerDay = isNewFormat ? 25 : 24;
  const totalDays = 7;

  const rawBuffer = new ArrayBuffer(mappedBinary.length / 2);
  const arrayedBinary = new Uint8Array(rawBuffer);

  // Parse the hex string into bytes
  for (let i = 0, j = 0; i < mappedBinary.length; i += 2, j++) {
    arrayedBinary[j] = parseInt(mappedBinary.substring(i, i + 2), 16);
  }

  const weekConfig: WeekConfiguration = {};

  dayNames.forEach((day, index) => {
    const dayStartIndex = index * blocksPerDay;
    let fullDayBinaryString = '';
    let startHour = null;
    let endHour = null;

    // Concatenate the binary strings of all blocks for the current day
    for (let block = 0; block < blocksPerDay; block++) {
      const byte = arrayedBinary[dayStartIndex + block];
      const binaryString = (byte !== undefined ? byte : 0)
        .toString(2)
        .padStart(8, '0');
      fullDayBinaryString += binaryString;
    }

    // Only use the relevant bits for the day
    fullDayBinaryString = fullDayBinaryString.slice(0, hoursPerDay);

    // Find the first and last occurrence of '1'
    const firstOneIndex = fullDayBinaryString.indexOf('1');
    const lastOneIndex = fullDayBinaryString.lastIndexOf('1');

    if (firstOneIndex !== -1) {
      startHour = firstOneIndex;
      endHour = lastOneIndex;
    }

    weekConfig[day] = {
      start: startHour !== null ? startHour : 0,
      end: endHour !== null ? endHour : 0,
    };
  });

  return weekConfig;
};
